/**
 * BIM/File Management API Service
 * Handles all API calls for 3D files, conversions, and document management
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const BIMService = {
  /**
   * Upload and convert RVT file
   * @param {File} file - The RVT file
   * @returns {Promise} - Conversion result
   */
  uploadAndConvertRVT: async (file) => {
    const formData = new FormData();
    formData.append('rvtFile', file);

    try {
      const response = await fetch(`${API_BASE_URL}/api/bim/upload-rvt`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('RVT conversion error:', error);
      throw error;
    }
  },

  /**
   * Upload 3D model file
   * @param {File} file - The model file (GLB, GLTF, OBJ, STL, etc.)
   * @returns {Promise} - Upload result
   */
  uploadModel: async (file) => {
    const formData = new FormData();
    formData.append('modelFile', file);

    try {
      const response = await fetch(`${API_BASE_URL}/api/bim/upload-model`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Model upload error:', error);
      throw error;
    }
  },

  /**
   * Get list of converted models
   * @returns {Promise} - Array of converted models
   */
  getConvertedModels: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/bim/models`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching converted models:', error);
      throw error;
    }
  },

  /**
   * Save painted model
   * @param {string} modelName - Name of the model
   * @param {Object} paintData - Paint data object
   * @param {string} originalModel - Original model path
   * @returns {Promise} - Save result
   */
  savePaintedModel: async (modelName, paintData, originalModel) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/bim/save-painted`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          modelName,
          paintData,
          originalModel,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error saving painted model:', error);
      throw error;
    }
  },

  /**
   * Get list of saved painted models
   * @returns {Promise} - Array of saved models
   */
  getSavedModels: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/bim/saved-models`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching saved models:', error);
      throw error;
    }
  },

  /**
   * Load saved painted model
   * @param {string} filename - Filename of saved model
   * @returns {Promise} - Saved model data
   */
  loadSavedModel: async (filename) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/bim/load-saved/${encodeURIComponent(filename)}`,
        {
          method: 'GET',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading saved model:', error);
      throw error;
    }
  },

  /**
   * Download file
   * @param {string} filePath - Path to the file
   * @param {string} fileName - Name for the downloaded file
   */
  downloadFile: async (filePath, fileName) => {
    try {
      const response = await fetch(`${API_BASE_URL}${filePath}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'download';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
      throw error;
    }
  },

  /**
   * Export painted model as JSON
   * @param {Object} paintData - Paint data to export
   * @param {string} fileName - Name for exported file
   */
  exportPaintData: (paintData, fileName = 'paint-data.json') => {
    const dataStr = JSON.stringify(paintData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  /**
   * Import paint data from JSON file
   * @param {File} file - JSON file containing paint data
   * @returns {Promise} - Parsed paint data
   */
  importPaintData: async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result);
          resolve(data);
        } catch (error) {
          reject(new Error('Invalid JSON file'));
        }
      };
      reader.onerror = () => reject(new Error('Error reading file'));
      reader.readAsText(file);
    });
  },
};

export default BIMService;
